/**
 * Disposable ResizeObserver function.
 *
 * Usage example:
 *
 * ```ts
 * useEffect(() => {
 *   return observeSize(ref.current, (size) => {
 *    console.log("Size changed:", size)
 *   })
 * }
 * ```
 */
export function observeSize(element: Element | Element[], callback: (size: { width: number; height: number }) => void) {
  if (!element) return () => {}

  const observer = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.contentRect) {
        callback({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        })
      }
    }
  })

  if (Array.isArray(element)) {
    element.forEach((el) => observer.observe(el))
  } else {
    observer.observe(element)
  }

  return () => observer.disconnect()
}

export function observeWindowSize(callback: (size: { width: number; height: number }) => void) {
  const onResize = () => {
    callback({
      width: window.innerWidth,
      height: window.innerHeight,
    })
  }
  window.addEventListener("resize", onResize)
  onResize()
  return () => {
    window.removeEventListener("resize", onResize)
  }
}
